import { useCallback, useEffect, useRef } from 'react'
import useSWRInfinite from 'swr/infinite'
import { motion } from 'framer-motion'
import lucideIcons from '@iconify-json/lucide/icons.json'
import { addCollection } from '@iconify/react'
import { CheckCircle } from 'lucide-react'
import { useTranslations } from 'next-intl'

import type { Asset } from '@/lib/types'
import { cn, fetcher } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { IconPlayload } from './types'
import { useEditorStore } from '@/hooks/use-editor-store'
import { LucideIcon } from '@/components/ui/lucide-icon'
import { useProjectStore } from '@/hooks/use-project-store'

// 注册 icon 集合
addCollection(lucideIcons)

interface IconLibraryProps {
  query: string
  selectedAssets: Asset[]
  onSelect: (e: React.MouseEvent, asset: Asset) => void
  limit?: number
  isExpanded?: boolean
}

interface SearchResultItem {
  id: string | number
  payload: IconPlayload
  score: number
}

interface SearchResult {
  items: SearchResultItem[]
  next_page_offset?: string
}

const PAGE_SIZE = 10

export const IconLibrary = ({
  query,
  selectedAssets,
  onSelect,
  limit,
  isExpanded = false,
}: IconLibraryProps) => {
  const t = useTranslations('icon-library')
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const { getIsEditorOn, getIsTargetIcon, setTargetPayload } = useEditorStore()
  const { setIsToolboxOpen } = useProjectStore()

  const isEditorOn = getIsEditorOn()
  const isIconSelectedInEditor = isEditorOn && getIsTargetIcon()

  const getKey = (
    pageIndex: number,
    previousPageData: SearchResult | null,
    query: string,
  ) => {
    if (previousPageData && !previousPageData.next_page_offset) return null
    return [
      'icon-search',
      pageIndex,
      previousPageData?.next_page_offset,
      query || '__default__',
    ]
  }

  const fetcherImplement = async ([_, pageIndex, next_page_offset, query]: [
    string,
    number,
    string | undefined,
    string,
  ]): Promise<SearchResult> => {
    const finalQuery = query === '__default__' ? '' : query
    const res = await fetcher('/api/media/icon', {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        query: finalQuery,
        limit: PAGE_SIZE,
        offset: next_page_offset,
      }),
    })

    return res.data
  }

  const { data, size, setSize, isLoading } = useSWRInfinite<SearchResult>(
    (index, prev) => getKey(index, prev, query),
    fetcherImplement,
    {
      revalidateFirstPage: false,
    },
  )

  const handleSelect = useCallback(
    (item: SearchResultItem, e: React.MouseEvent) => {
      if (isEditorOn && !isIconSelectedInEditor) return
      const { payload } = item

      const assetData: Asset = {
        source: 'elements',
        type: 'icon',
        link: payload.name,
        thumbnail: '',
        payload: {
          componentName: payload.name,
          // @ts-ignore
          ...lucideIcons.icons?.[payload.name],
        },
      }

      if (isIconSelectedInEditor) {
        setIsToolboxOpen(false)
        setTargetPayload(assetData.payload)
      } else {
        onSelect(e, assetData)
      }
    },
    [
      onSelect,
      isEditorOn,
      isIconSelectedInEditor,
      setIsToolboxOpen,
      setTargetPayload,
    ],
  )

  // 列表数据
  const items = data ? data.flatMap((d) => d.items) : []
  const isLoadingMore = isLoading || (size > 0 && !data?.[size - 1])
  const hasMore = data?.[data.length - 1]?.next_page_offset
  const isEmpty = data?.[0]?.items.length === 0
  const isReachingEnd = isEmpty || (data && !hasMore)

  useEffect(() => {
    if (!loadMoreRef.current || isLoadingMore || isReachingEnd || limit) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setSize(size + 1)
        }
      },
      { rootMargin: '50px' },
    )

    observer.observe(loadMoreRef.current)
    return () => observer.disconnect()
  }, [isLoadingMore, isReachingEnd, setSize, size, limit])

  if (isLoadingMore && size === 1) {
    return (
      <div className="text-center py-4 col-span-full">
        <p className="text-sm text-gray-500">{t('loading')}</p>
      </div>
    )
  }

  if (isEmpty) {
    return (
      <div className="text-center py-4 col-span-full">
        <p className="text-sm text-gray-500">{t('no_audios')}</p>
      </div>
    )
  }

  return (
    <>
      {items.map((item) => (
        <div
          key={item.id}
          className={cn(
            'flex-none relative size-16 ',
            isEditorOn && !isIconSelectedInEditor
              ? 'opacity-50 cursor-not-allowed'
              : 'cursor-pointer',
          )}
          onClick={(e) => handleSelect(item, e)}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="absolute inset-0 rounded-lg overflow-hidden group">
                <LucideIcon icon={item.payload.name} />
              </div>
            </TooltipTrigger>
            <TooltipContent
              side="left"
              className="bg-gray-800 text-white border-gray-600"
            >
              <p>{item.payload.name}</p>
            </TooltipContent>
          </Tooltip>
          {selectedAssets.some((asset) => asset.link === item.payload.name) && (
            <div className="absolute top-0 left-0 w-full h-full cursor-pointer">
              <div className="bg-gray-900 opacity-[0.1] w-full h-full" />
              <div className="absolute top-0 left-0 bg-brand text-white rounded-full p-1">
                <CheckCircle className="w-4 h-4" />
              </div>
            </div>
          )}
        </div>
      ))}
      {!limit && <div ref={loadMoreRef} className="h-4" />}
      {isLoadingMore && !limit && (
        <div className="text-center py-4 col-span-full">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('loading_more')}
          </p>
        </div>
      )}
    </>
  )
}
